import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperIns } from 'swiper';
import IconFont from '$/components/IconFont';
import type { OfficialAssetServerMaterial } from '$/api/server.types';
import { handleImageUrl } from '$/utils/image';
import { useLanguageStore } from '$/store/languageStore';

import styles from './index.module.scss';
import type { OfficialAssetMaterialImage } from '../types';
import 'swiper/css';

const MATERIAL_DETAIL_PREVIEW_SIZE = 384;
const MATERIAL_DETAIL_THUMB_SIZE = 60;

export interface MaterialDetailPanelProps {
  material: OfficialAssetServerMaterial | null;
  images: OfficialAssetMaterialImage[];
  showMergeApplyButton: boolean;
  applyingToCanvas: boolean;
  mergingToCanvas: boolean;
  applyDisabledReason: string;
  onApplyToCanvas: () => void;
  onMergeApplyToCanvas: () => void;
  actionLabel?: string;
  actionDisabled?: boolean;
}

/**
 * 官方资产详情侧栏，按 Figma 固定在资产列表右侧展示素材图片、标签和操作入口。
 */
function MaterialDetailPanel({
  material,
  images,
  showMergeApplyButton,
  applyingToCanvas,
  mergingToCanvas,
  applyDisabledReason,
  onApplyToCanvas,
  onMergeApplyToCanvas,
  actionLabel,
  actionDisabled = false,
}: MaterialDetailPanelProps): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const swiperRef = useRef<SwiperIns | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const safeActiveImageIndex = Math.min(activeImageIndex, Math.max(images.length - 1, 0));
  const hasPreviousImage = safeActiveImageIndex > 0;
  const hasNextImage = safeActiveImageIndex < images.length - 1;
  const operationInProgress = applyingToCanvas || mergingToCanvas;
  const applyButtonDisabled = operationInProgress || Boolean(applyDisabledReason) || actionDisabled;

  /** 切换素材时重置详情图索引，避免沿用上一个素材的轮播位置。 */
  useEffect(() => {
    setActiveImageIndex(0);
  }, [material?.id]);

  if (material == null) {
    return (
      <aside className={styles.materialDetailLayer}>
        <p className={styles.materialDetailTitle}>
          {languageStore.smartcatTranslationText((v) => v.material_details)}
        </p>
        <div className={styles.materialDetailEmptyState}>
          <div className={styles.materialDetailEmptyContent}>
            <div className={styles.materialDetailEmptyIconWrap}>
              <IconFont
                name="fourPointedStarFill"
                fontSize={20}
                color="var(--content-primary, rgb(247 248 250 / 96%))"
                className={styles.materialDetailEmptyIcon}
              />
            </div>
            <div className={styles.materialDetailEmptyText}>
              <p className={styles.materialDetailEmptyTitle}>
                {languageStore.smartcatTranslationText((v) => v.select_material_to_view_details)}
              </p>
              <p className={styles.materialDetailEmptyDescription}>
                {languageStore.smartcatTranslationText(
                  (v) => v.after_clicking_any_material_card_a_preview_and_material_information_will_be_displayed_here_,
                )}
              </p>
            </div>
          </div>
        </div>
      </aside>
    );
  }

  /** 保存 Swiper 实例，供右侧箭头和缩略图同步切换。 */
  const handleSwiper = (swiper: SwiperIns): void => {
    swiperRef.current = swiper;
  };

  /** 主图滑动变化时同步当前缩略图选中态。 */
  const handleSlideChange = (swiper: SwiperIns): void => {
    if (operationInProgress) {
      return;
    }
    setActiveImageIndex(swiper.activeIndex);
  };

  /** 点击缩略图时切换到对应图片。 */
  const handleThumbnailClick = (index: number): void => {
    if (operationInProgress) {
      return;
    }
    swiperRef.current?.slideTo(index);
    setActiveImageIndex(index);
  };

  /** 点击左侧悬浮箭头切换上一张素材图。 */
  const handlePreviousImage = (): void => {
    if (operationInProgress || !hasPreviousImage) {
      return;
    }
    swiperRef.current?.slidePrev();
  };

  /** 点击右侧悬浮箭头切换下一张素材图。 */
  const handleNextImage = (): void => {
    if (operationInProgress || !hasNextImage) {
      return;
    }
    swiperRef.current?.slideNext();
  };

  /** 点击主按钮时应用当前素材全部图片，禁用原因由父组件统一计算。 */
  const handleApplyClick = (): void => {
    if (applyButtonDisabled) {
      return;
    }
    onApplyToCanvas();
  };

  /** 点击次按钮时先合并当前素材全部图片，再应用唯一的合并图。 */
  const handleMergeApplyClick = (): void => {
    if (applyButtonDisabled) {
      return;
    }
    onMergeApplyToCanvas();
  };

  return (
    <aside className={styles.materialDetailLayer}>
      <p className={styles.materialDetailTitle}>
        {languageStore.smartcatTranslationText((v) => v.material_details)}
      </p>
      <div className={styles.materialDetailPreview}>
        <Swiper
          key={material.id}
          className={styles.materialDetailSwiper}
          slidesPerView="auto"
          centeredSlides
          centeredSlidesBounds
          centerInsufficientSlides={false}
          spaceBetween={10}
          speed={240}
          allowTouchMove={!operationInProgress}
          onSwiper={handleSwiper}
          onSlideChange={handleSlideChange}
        >
          {images.map((image) => (
            <SwiperSlide key={image.sourceKey} className={styles.materialDetailSlide}>
              <div className={styles.materialDetailImageBox}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className={styles.materialDetailImage}
                  src={handleImageUrl(image.url, { size: MATERIAL_DETAIL_PREVIEW_SIZE })}
                  alt={image.label}
                  crossOrigin="anonymous"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        {hasPreviousImage && (
          <button
            type="button"
            className={cn(styles.materialDetailNavButton, styles.materialDetailPrevButton)}
            disabled={operationInProgress}
            onClick={handlePreviousImage}
          >
            <IconFont name="arrowLeftBold" fontSize={16} color="currentColor" />
          </button>
        )}
        {hasNextImage && (
          <button
            type="button"
            className={cn(styles.materialDetailNavButton, styles.materialDetailNextButton)}
            disabled={operationInProgress}
            onClick={handleNextImage}
          >
            <IconFont name="arrowRightBold" fontSize={16} color="currentColor" />
          </button>
        )}
      </div>
      <div className={styles.materialDetailThumbList}>
        {images.map((image, index) => {
          const selected = index === safeActiveImageIndex;
          return (
            <button
              key={image.sourceKey}
              type="button"
              className={cn(styles.materialDetailThumbItem, { [styles.materialDetailThumbItemSelected]: selected })}
              disabled={operationInProgress}
              onClick={() => handleThumbnailClick(index)}
            >
              <span className={styles.materialDetailThumbImageWrap}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className={styles.materialDetailThumbImage}
                  src={handleImageUrl(image.url, { mode: 3, width: MATERIAL_DETAIL_THUMB_SIZE, height: MATERIAL_DETAIL_THUMB_SIZE })}
                  alt={image.label}
                  crossOrigin="anonymous"
                />
              </span>
              <span className={styles.materialDetailThumbLabel}>{image.label}</span>
            </button>
          );
        })}
      </div>
      <div className={styles.materialDetailMetaScroll}>
        <div className={styles.materialDetailSeparator} />
        <div className={styles.materialDetailInfo}>
          <div className={styles.materialDetailInfoRow}>
            <span className={styles.materialDetailInfoLabel}>
              {languageStore.smartcatTranslationText((v) => v.roboneo_web_name)}
            </span>
            <span className={styles.materialDetailInfoValue}>{material.name}</span>
          </div>
          <div className={styles.materialDetailInfoRow}>
            <span className={styles.materialDetailInfoLabel}>
              {languageStore.smartcatTranslationText((v) => v.label)}
            </span>
            <div className={styles.materialDetailTags}>
              {material.tags?.map((tag) => (
                <span key={tag} className={styles.materialDetailTag}>{tag}</span>
              ))}
            </div>
          </div>
          {/* 素材描述按当前产品要求暂不展示。
          <div className={styles.materialDetailInfoRow}>
            <span className={styles.materialDetailInfoLabel}>
              {languageStore.smartcatTranslationText((v) => v.roboneo_web_description)}
            </span>
            <span className={styles.materialDetailInfoDescription}>{material.description}</span>
          </div>
          */}
        </div>
      </div>
      <div className={styles.materialDetailFooter}>
        <div className={styles.materialDetailSeparator} />
        <div className={styles.materialDetailActions}>
          {showMergeApplyButton && (
            <button
              type="button"
              className={styles.materialDetailSecondaryButton}
              disabled={applyButtonDisabled}
              title={applyDisabledReason || undefined}
              onClick={handleMergeApplyClick}
            >
              {mergingToCanvas && (
                <IconFont
                  name="loadingCircleBold"
                  fontSize={14}
                  color="currentColor"
                  className={styles.materialDetailButtonLoadingIcon}
                />
              )}
              <span className={styles.materialDetailButtonText}>
                {mergingToCanvas
                  ? languageStore.smartcatTranslationText((v) => v.processing)
                  : languageStore.smartcatTranslationText((v) => v.apply_to_canvas_after_merging)}
              </span>
            </button>
          )}
          <button
            type="button"
            className={styles.materialDetailPrimaryButton}
            disabled={applyButtonDisabled}
            title={applyDisabledReason || undefined}
            onClick={handleApplyClick}
          >
            {applyingToCanvas && (
              <IconFont
                name="loadingCircleBold"
                fontSize={14}
                color="currentColor"
                className={styles.materialDetailButtonLoadingIcon}
              />
            )}
            <span className={styles.materialDetailButtonText}>
              {applyingToCanvas
                ? languageStore.smartcatTranslationText((v) => v.processing)
                : actionLabel || languageStore.smartcatTranslationText((v) => v.applied_to_canvas)}
            </span>
          </button>
        </div>
      </div>
    </aside>
  );
}

export default MaterialDetailPanel;
